import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Ban } from "lucide-react";
import { WOStatusWorkflow } from "./WOStatusWorkflow";
import { useState } from "react";
import { toast } from "sonner";
import { renumberWorkOrder } from "@/lib/api";
import { STATUS_LABELS, STATUS_COLORS } from "./workOrderTabUtils";

// iter331 — double-click the WO# on a saved WO to edit it (office roles).
export function WONumberEditable({ form, woId, onRenumbered, className = "" }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const canEdit = !!woId && form.foundation_sp_push_status !== "success";

  const commit = async () => {
    const next = value.trim().toUpperCase();
    if (!next || next === form.wo_number) { setEditing(false); return; }
    setSaving(true);
    try {
      const res = await renumberWorkOrder(woId, next);
      toast.success(`WO# changed to ${res.data?.wo_number || next}`);
      onRenumbered?.(res.data);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change the WO#");
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <Input
        autoFocus
        value={value}
        disabled={saving}
        onChange={(e) => setValue(e.target.value.toUpperCase())}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
        }}
        className="h-7 w-32 font-mono font-bold text-brand text-sm inline-block"
        data-testid="wo-number-edit-input"
      />
    );
  }
  return (
    <span
      className={`font-mono font-bold text-brand ${className} ${canEdit ? "cursor-text hover:bg-slate-100 px-1 -mx-1 rounded" : ""}`}
      title={canEdit ? "Double-click to change the WO#" : form.foundation_sp_push_status === "success" ? "Already a Foundation job — number is locked" : ""}
      onDoubleClick={() => { if (canEdit) { setValue(form.wo_number || ""); setEditing(true); } }}
      data-testid="wo-number-form"
    >
      {form.wo_number || "Auto"}
    </span>
  );
}

// Stateless header for the WO edit form. Renders the Back button, the
// auto-save status pill, the colored status chip, the WO number, and
// the workflow-step progress strip. Extracted from WorkOrderTab.jsx
// (Feb 2026, iter174) to chip away at the file's size — this block
// had no state of its own, only props.
export function WOFormHeader({ form, woSaveStatus, onBack, woId, onRenumbered }) {
  const cancelledAt = form.status === "cancelled" && form.cancelled_at
    ? new Date(form.cancelled_at).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })
    : "";

  return (
    <>
      {/* iter270 — a cancelled WO has to be unmistakable, with the reason,
          who cancelled it and when right at the top. */}
      {form.status === "cancelled" && (
        <div className="flex items-start gap-2.5 rounded-lg border-2 border-red-300 bg-red-50 px-4 py-3 mb-2"
             data-testid="wo-cancelled-banner">
          <Ban className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-sm font-bold text-red-700">
              Cancelled{cancelledAt ? ` · ${cancelledAt}` : ""}{form.cancelled_by ? ` · ${form.cancelled_by}` : ""}
            </div>
            <div className="text-xs text-red-800 mt-0.5 break-words">
              {form.cancellation_reason || "No reason recorded."}
            </div>
            <div className="text-[11px] text-red-600/80 mt-1">
              Scheduled visits were removed from the technicians' schedules. This work order does not count as won revenue.
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="btn-wo-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-3">
          {woSaveStatus && (
            <span className="text-xs text-slate-400 italic" data-testid="wo-auto-save-status">
              {woSaveStatus}
            </span>
          )}
          <span
            className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[form.status] || ""}`}
            data-testid="wo-status-badge"
          >
            {STATUS_LABELS[form.status] || form.status}
          </span>
          <div className="text-right">
            <span className="text-xs text-slate-500">WO# </span>
            <WONumberEditable form={form} woId={woId} onRenumbered={onRenumbered} />
          </div>
        </div>
      </div>

      <WOStatusWorkflow form={form} className="gap-1 px-2" />
    </>
  );
}
